import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    ArrowLeft, Search, X, Upload, Target, Settings, Plus, BookOpen, XCircle,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { exportImportService } from '../services/exportImport.service';
import CustomStudyInline from '../components/deck_subdeck/CustomStudyInline';
import Toast from '../components/Toast';
import UpgradeModal from '../components/dashboard/UpgradeModal';
import InterdictedModal from '../components/AccessControl/InterdictedModal';
import DeckIconManager from '../components/deck_subdeck/DeckIconManager';
import ImportModal from '../components/deck_subdeck/ImportModal';
import CycleTabs from '../components/deck_subdeck/CycleTabs';
import TabManagerModal from '../components/deck_subdeck/TabManagerModal';
import DeckGrid, { DeckItem, DECK_COLOR_OPTIONS } from '../components/deck_subdeck/DeckGrid';
import DeckIconPicker from '../components/deck_subdeck/DeckIconPicker';
import type { Tab } from '../components/deck_subdeck/CycleTabs';
import MoveModal from '../components/deck_subdeck/MoveModal';
import CopyModal from '../components/deck_subdeck/CopyModal';
import HeaderPattern, { HEADER_THEMES } from '../components/shared/HeaderPattern';
import './CycleDecksPage.css';

const API = '/api';

interface Cycle {
    _id: string;
    name: string;
    description: string;
    color: string;
    headerTheme?: string;
    skipDeckLevel?: boolean;
    singleDeckId?: string;
    isFree?: boolean;
    masteryPercentage?: number;
}

interface LastStudiedDeck {
    deckId: string;
    deckName: string;
    lastStudiedAt: string;
    masteryPercentage: number;
}

function formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHr / 24);
    if (diffMin < 1) return 'agora mesmo';
    if (diffMin < 60) return `há ${diffMin} min`;
    if (diffHr < 24) return `há ${diffHr}h`;
    if (diffDays < 7) return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
    const weeks = Math.floor(diffDays / 7);
    return `há ${weeks} semana${weeks > 1 ? 's' : ''}`;
}

const DeckCardSkeleton: React.FC = () => (
    <div className="skeleton-card">
        <div className="skeleton skeleton-title" />
        <div className="skeleton skeleton-description" />
        <div className="skeleton skeleton-description" style={{ width: '70%' }} />
        <div className="skeleton-stats">
            <div className="skeleton skeleton-stat" />
            <div className="skeleton skeleton-stat" />
        </div>
        <div className="skeleton" style={{ height: '40px', marginTop: '8px' }} />
    </div>
);

const CycleDecksPage: React.FC = () => {
    const { cycleId } = useParams<{ cycleId: string }>();
    const navigate = useNavigate();
    const { isAdmin, isEditor } = useAuth();

    // ── Core state ──
    const [cycle, setCycle] = useState<Cycle | null>(null);
    const [decks, setDecks] = useState<DeckItem[]>([]);
    const [tabs, setTabs] = useState<Tab[]>([]);
    const [activeTabId, setActiveTabId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingTabs, setLoadingTabs] = useState(true);
    const [lastStudiedDeck, setLastStudiedDeck] = useState<LastStudiedDeck | null>(null);

    // ── UI state ──
    const [searchQuery, setSearchQuery] = useState('');
    const [showStudyMode, setShowStudyMode] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    // ── Modals ──
    const [showTabManager, setShowTabManager] = useState(false);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showInterdictedModal, setShowInterdictedModal] = useState(false);
    const [showIconDialog, setShowIconDialog] = useState(false);
    const [showAdminMenu, setShowAdminMenu] = useState(false);
    const adminMenuRef = useRef<HTMLDivElement>(null);
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [selectedTheme, setSelectedTheme] = useState('default');
    const [themeSaving, setThemeSaving] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [deckToMove, setDeckToMove] = useState<DeckItem | null>(null);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [deckToCopy, setDeckToCopy] = useState<DeckItem | null>(null);

    // ── Deck form ──
    const [currentDeck, setCurrentDeck] = useState<DeckItem | null>(null);
    const [deckName, setDeckName] = useState('');
    const [deckDescription, setDeckDescription] = useState('');
    const [deckIsFree, setDeckIsFree] = useState(false);
    const [deckColorId, setDeckColorId] = useState('blue-default');
    const [deckIconId, setDeckIconId] = useState<string | null>(null);
    const [deckTags, setDeckTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [selectedDeck, setSelectedDeck] = useState<DeckItem | null>(null);
    const [selectedEntityForModal, setSelectedEntityForModal] = useState<{ type: 'deck' | 'subdeck'; name: string } | null>(null);

    // ── Import ──
    const [importFiles, setImportFiles] = useState<File[]>([]);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState('');

    // ── Custom study cache ──
    const [customStudyCacheDecks, setCustomStudyCacheDecks] = useState<any[] | null>(null);
    const [customStudyCacheSubdecksMap, setCustomStudyCacheSubdecksMap] = useState<Map<string, any[]> | null>(null);

    const token = () => localStorage.getItem('accessToken') || '';

    const clearCache = useCallback(() => {
        sessionStorage.removeItem(`cycleDecks_v2_${cycleId}`);
        sessionStorage.removeItem(`cycleDecks_${cycleId}`);
        sessionStorage.removeItem('dashboardData');
    }, [cycleId]);

    // ── Load cycle data ──
    const loadCycleData = useCallback(async () => {
        if (!cycleId) return;
        const cacheKey = `cycleDecks_v2_${cycleId}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            const p = JSON.parse(cached);
            if (Date.now() - p.timestamp < 600000) {
                setCycle(p.cycle);
                setDecks(p.decks || []);
                setLastStudiedDeck(p.lastStudiedDeck || null);
                setLoading(false);
                return;
            }
        }
        try {
            setLoading(true);
            const res = await fetch(`${API}/cycles/${cycleId}/decks`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCycle(data.cycle || data);
                setDecks(data.decks || []);
                setLastStudiedDeck(data.lastStudiedDeck || null);
                sessionStorage.setItem(cacheKey, JSON.stringify({
                    cycle: data.cycle || data,
                    decks: data.decks || [],
                    lastStudiedDeck: data.lastStudiedDeck || null,
                    timestamp: Date.now()
                }));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [cycleId]);

    // ── Load tabs ──
    const loadTabs = useCallback(async () => {
        if (!cycleId) return;
        try {
            setLoadingTabs(true);
            const t = localStorage.getItem('accessToken') || '';
            const res = await fetch(`${API}/cycles/${cycleId}/tabs`, {
                headers: { Authorization: `Bearer ${t}` }
            });
            if (res.ok) {
                const data: Tab[] = await res.json();
                setTabs(data);
                const savedTabId = sessionStorage.getItem(`activeTab_${cycleId}`);
                if (savedTabId && data.some(tab => tab._id === savedTabId)) {
                    setActiveTabId(savedTabId);
                } else if (data.length > 0) {
                    setActiveTabId(data[0]._id);
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingTabs(false);
        }
    }, [cycleId]);

    // ── Prefetch custom study ──
    const prefetchCustomStudy = useCallback(async () => {
        if (customStudyCacheDecks !== null) return;
        try {
            const res = await fetch(`${API}/cycles/${cycleId}/decks`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                const data = await res.json();
                const allDecks = (data.decks || [])
                    .filter((d: any) => d.purchased && !d.isBlocked)
                    .map((d: any) => ({
                        _id: d._id, name: d.name,
                        description: d.description || '',
                        subdeckCount: d.subdeckCount || 0,
                    }));
                setCustomStudyCacheDecks(allDecks);
                const entries = await Promise.all(allDecks.map(async (deck: any) => {
                    try {
                        const r = await fetch(`${API}/decks/${deck._id}/subdecks-meta`, {
                            headers: { Authorization: `Bearer ${token()}` }
                        });
                        if (r.ok) {
                            const d = await r.json();
                            return [deck._id, (d.subdecks || d)
                                .filter((s: any) => s.type !== 'apostila')
                                .map((s: any) => ({
                                    _id: s._id, name: s.name,
                                    cardCount: s.cardCount || 0,
                                    groupId: s.groupId || null,
                                }))] as [string, any[]];
                        }
                    } catch { }
                    return [deck._id, []] as [string, any[]];
                }));
                setCustomStudyCacheSubdecksMap(new Map(entries));
            }
        } catch (e) {
            console.error(e);
        }
    }, [customStudyCacheDecks, cycleId]);

    useEffect(() => {
        loadCycleData();
        loadTabs();
        setTimeout(prefetchCustomStudy, 1000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Redirecionar automaticamente se este campo pula o nível de deck
    useEffect(() => {
        if (cycle?.skipDeckLevel && cycle?.singleDeckId) {
            const ctx = {
                from: 'cycle', accessMode: 'read', cycleId,
                returnPath: '/dashboard',
                returnState: { activePage: 'explore', activeExploreTab: 'content' },
                purchased: true, allowFreeEntry: cycle.isFree || false,
            };
            sessionStorage.setItem('subdecksContext', JSON.stringify(ctx));
            navigate(`/subdecks/${cycle.singleDeckId}`, { state: ctx, replace: true });
        }
    }, [cycle, cycleId, navigate]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (adminMenuRef.current && !adminMenuRef.current.contains(e.target as Node)) {
                setShowAdminMenu(false);
            }
        };
        if (showAdminMenu) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showAdminMenu]);

    const handleTabChange = (tabId: string) => {
        setActiveTabId(tabId);
        setShowStudyMode(false);
        sessionStorage.setItem(`activeTab_${cycleId}`, tabId);
    };

    const handleStudyToggle = () => {
        setShowStudyMode(prev => !prev);
        if (!showStudyMode) {
            setCustomStudyCacheDecks(null);
            setCustomStudyCacheSubdecksMap(null);
        }
    };

    const handleThemeSave = async () => {
        setThemeSaving(true);
        try {
            await fetch(`/api/cycles/${cycleId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({ headerTheme: selectedTheme })
            });
            setCycle(prev => prev ? { ...prev, headerTheme: selectedTheme } : prev);
            const cacheKey = `cycleDecks_v2_${cycleId}`;
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                const p = JSON.parse(cached);
                if (p.cycle) p.cycle.headerTheme = selectedTheme;
                sessionStorage.setItem(cacheKey, JSON.stringify(p));
            }
        } catch { /* ignore */ }
        setThemeSaving(false);
        setShowThemeModal(false);
    };

    // ── Navegação para subdecks ──
    const handleViewSubdecks = (deck: DeckItem) => {
        const context = {
            from: 'cycle', accessMode: 'read', cycleId,
            returnPath: `/cycles/${cycleId}/decks`,
            purchased: deck.purchased || false,
            allowFreeEntry: deck.allowFreeEntry || false
        };
        sessionStorage.setItem('subdecksContext', JSON.stringify(context));
        if (activeTabId) sessionStorage.setItem(`activeTab_${cycleId}`, activeTabId);
        navigate(`/subdecks/${deck._id}`, { state: context });
    };

    const handleContinueLastDeck = () => {
        if (!lastStudiedDeck) return;
        const context = {
            from: 'cycle', accessMode: 'read', cycleId,
            returnPath: `/cycles/${cycleId}/decks`,
            purchased: true, allowFreeEntry: false
        };
        sessionStorage.setItem('subdecksContext', JSON.stringify(context));
        navigate(`/subdecks/${lastStudiedDeck.deckId}`, { state: context });
    };

    const handleToggleFree = async (deckId: string) => {
        try {
            const res = await fetch(`${API}/decks/${deckId}/toggle-free`, {
                method: 'PATCH', headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                const data = await res.json();
                setDecks(prev => prev.map(d => d._id === deckId ? { ...d, isFree: data.isFree } : d));
                setToast({ message: data.message, type: 'success' });
                clearCache();
            }
        } catch { setToast({ message: 'Erro ao alternar gratuidade', type: 'error' }); }
    };

    // ── CRUD decks ──
    const closeAllDialogs = () => {
        setShowCreateDialog(false);
        setShowEditDialog(false);
        setShowDeleteDialog(false);
        setShowIconDialog(false);
        setCurrentDeck(null);
        setDeckName('');
        setDeckDescription('');
        setDeckTags([]);
        setTagInput('');
        setDeckIsFree(false);
        setDeckColorId('blue-default');
        setDeckIconId(null);
        setSubmitting(false);
    };

    const handleCreateDeck = async () => {
        if (!deckName.trim()) return;
        try {
            setSubmitting(true);
            const res = await fetch(`${API}/cycles/${cycleId}/decks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({
                    name: deckName, description: deckDescription,
                    isFree: deckIsFree, tags: deckTags,
                    colorId: deckColorId,
                    iconId: deckIconId,
                    tabId: tabs.length > 0 ? activeTabId : undefined
                })
            });
            if (res.ok) {
                const newDeck = await res.json();
                clearCache();
                await loadTabs();
                setDecks(prev => [...prev, {
                    _id: newDeck._id, name: newDeck.name,
                    description: newDeck.description || '',
                    iconUrl: null, subdeckCount: 0, totalCards: 0,
                    isBlocked: false, isFree: newDeck.isFree || false,
                    purchased: true, tags: newDeck.tags || [],
                    colorId: deckColorId,
                    createdAt: newDeck.createdAt, updatedAt: newDeck.createdAt
                }]);
                closeAllDialogs();
                setToast({ message: 'Deck criado', type: 'success' });
            }
        } catch { setToast({ message: 'Erro ao criar deck', type: 'error' }); }
        finally { setSubmitting(false); }
    };

    const handleEditDeck = async () => {
        if (!currentDeck || !deckName.trim()) return;
        try {
            setSubmitting(true);
            const res = await fetch(`${API}/decks/${currentDeck._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({ name: deckName, description: deckDescription, tags: deckTags, colorId: deckColorId, iconId: deckIconId })
            });
            if (res.ok) {
                setDecks(prev => prev.map(d => d._id === currentDeck!._id
                    ? { ...d, name: deckName, description: deckDescription, tags: deckTags, colorId: deckColorId }
                    : d
                ));
                clearCache();
                await loadCycleData();
                await loadTabs();
                closeAllDialogs();
                setToast({ message: 'Deck atualizado', type: 'success' });
            }
        } catch { setToast({ message: 'Erro ao atualizar', type: 'error' }); }
        finally { setSubmitting(false); }
    };

    const handleDeleteDeck = async () => {
        if (!currentDeck) return;
        try {
            setSubmitting(true);
            const res = await fetch(`${API}/decks/${currentDeck._id}`, {
                method: 'DELETE', headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                clearCache();
                await loadCycleData();
                await loadTabs();
                closeAllDialogs();
                setToast({ message: 'Deck excluído', type: 'success' });
            }
        } catch { setToast({ message: 'Erro ao excluir', type: 'error' }); }
        finally { setSubmitting(false); }
    };

    const handleToggleBlock = async (deckId: string) => {
        try {
            const res = await fetch(`${API}/decks/${deckId}/toggle-block`, {
                method: 'PATCH', headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                const data = await res.json();
                setDecks(prev => prev.map(d => d._id === deckId ? { ...d, isBlocked: data.deck.isBlocked } : d));
                setToast({ message: data.message, type: 'success' });
                clearCache();
            }
        } catch { setToast({ message: 'Erro', type: 'error' }); }
    };

    const handleToggleHide = async (deckId: string) => {
        try {
            const res = await fetch(`${API}/decks/${deckId}/toggle-hide`, {
                method: 'PATCH', headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                const data = await res.json();
                setDecks(prev => prev.map(d => d._id === deckId ? { ...d, isHidden: data.deck.isHidden } : d));
                setToast({ message: data.message, type: 'success' });
                clearCache();
            }
        } catch { setToast({ message: 'Erro', type: 'error' }); }
    };

    const handleExport = async (deckId: string) => {
        try {
            await exportImportService.exportDeck(deckId);
            setToast({ message: 'Deck exportado', type: 'success' });
        } catch { setToast({ message: 'Erro ao exportar', type: 'error' }); }
    };

    const handleImport = async () => {
        const importFile = importFiles[0];
        if (!importFile) return;
        try {
            setImporting(true);
            const { apostilasWithoutContent } = await exportImportService.importDeck(importFile, cycleId, setImportProgress);
            clearCache();
            await loadCycleData();
            await loadTabs();
            setToast(
                apostilasWithoutContent > 0
                    ? {
                        message: `Deck importado — ${apostilasWithoutContent} apostila(s) sem conteúdo (reimporte via "Importar apostila")`,
                        type: 'info'
                    }
                    : { message: 'Deck importado', type: 'success' }
            );
            setShowImportDialog(false);
            setImportFiles([]);
        } catch (e: any) {
            setToast({ message: `Erro: ${e.message}`, type: 'error' });
        } finally { setImporting(false); setImportProgress(''); }
    };

    const handleReorder = async (reordered: DeckItem[]) => {
        if (hasTabs && activeTabId) {
            setTabs(prev => prev.map(t =>
                t._id === activeTabId ? { ...t, decks: reordered } : t
            ));
            try {
                await fetch(`${API}/cycles/${cycleId}/tabs/${activeTabId}/decks-reorder`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                    body: JSON.stringify({ deckIds: reordered.map(d => d._id) })
                });
                clearCache();
            } catch {
                setToast({ message: 'Erro ao salvar ordem', type: 'error' });
            }
        } else {
            setDecks(reordered);
            try {
                await fetch(`${API}/decks/reorder`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                    body: JSON.stringify({ decks: reordered.map((d, i) => ({ _id: d._id, order: i })) })
                });
                clearCache();
            } catch {
                setToast({ message: 'Erro ao salvar ordem', type: 'error' });
            }
        }
    };

    const openEditDialog = (deck: DeckItem) => {
        setCurrentDeck(deck);
        setDeckName(deck.name);
        setDeckDescription(deck.description || '');
        setDeckTags(deck.tags || []);
        setDeckColorId(deck.colorId || 'blue-default');
        setDeckIconId(deck.iconId || null);
        setShowEditDialog(true);
    };

    const openDeleteDialog = (deck: DeckItem) => {
        setCurrentDeck(deck);
        setShowDeleteDialog(true);
    };

    const addTag = () => {
        const t = tagInput.trim();
        if (t && !deckTags.includes(t)) setDeckTags(prev => [...prev, t]);
        setTagInput('');
    };

    const removeTag = (tag: string) => setDeckTags(prev => prev.filter(t => t !== tag));

    // ── Dados derivados ──
    const hasTabs = tabs.length > 0;
    const activeTab = tabs.find(t => t._id === activeTabId);
    const activeTabDecks: DeckItem[] = (activeTab?.decks || []) as DeckItem[];

    // ── Loading ──
    if (loading || loadingTabs) return (
        <div className="cycle-decks-page">
            <div className="cdp-hero-header">
                <div className="cdp-hero-deco-clip"><HeaderPattern theme="default" /></div>
                <div className="cdp-hero-top-row">
                    <button className="cdp-btn-back" onClick={() => navigate('/dashboard', { state: { activePage: 'explore', activeExploreTab: 'content' } })}>
                        <ArrowLeft size={17} />
                    </button>
                </div>
                <div className="cdp-hero-meta">
                    <div className="skeleton" style={{ height: '28px', width: '220px', borderRadius: '6px', background: 'rgba(255,255,255,0.15)' }} />
                </div>
            </div>
            <div className="decks-container">
                <div className="dg-grid">
                    <DeckCardSkeleton /><DeckCardSkeleton /><DeckCardSkeleton /><DeckCardSkeleton />
                </div>
            </div>
        </div>
    );

    if (!cycle) return (
        <div className="cycle-decks-page">
            <div className="error-container">
                <h2>Ciclo não encontrado</h2>
                <button className="btn-primary" onClick={() => navigate('/dashboard', { state: { activePage: 'explore', activeExploreTab: 'content' } })}>Voltar</button>
            </div>
        </div>
    );

    return (
        <div className="cycle-decks-page">

            {/* ══ HEADER GRADIENTE ══ */}
            <div className="cdp-hero-header">
                {/* Deco em wrapper próprio para não vazar o overflow pro dropdown */}
                <div className="cdp-hero-deco-clip">
                    <HeaderPattern theme={cycle?.headerTheme || 'default'} />
                </div>

                {/* Linha 1: grid [Voltar] [Título centro] [Widget] */}
                <div className="cdp-hero-top-row">
                    <button className="cdp-btn-back" onClick={() => navigate('/dashboard', { state: { activePage: 'explore', activeExploreTab: 'content' } })}>
                        <ArrowLeft size={17} />
                    </button>

                    <div className="cdp-hero-meta">
                        <h1 className="cdp-hero-title">{cycle.name}</h1>
                        <p className="cdp-hero-subtitle">
                            {decks.length} deck{decks.length !== 1 ? 's' : ''}
                            {(cycle.masteryPercentage ?? 0) > 0 && (
                                <span className="cdp-hero-mastery-badge">
                                    · {cycle.masteryPercentage}% dominado
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="cdp-hero-actions">
                        <button
                            className={`cdp-study-widget ${showStudyMode ? 'cdp-study-widget--active' : ''}`}
                            onClick={handleStudyToggle}
                        >
                            <div className="cdp-study-widget-icon">
                                {showStudyMode ? <XCircle size={16} /> : <Target size={16} />}
                            </div>
                            <div className="cdp-study-widget-text">
                                <span className="cdp-study-widget-title">Estudo Personalizado</span>
                                <span className="cdp-study-widget-sub">
                                    {showStudyMode ? 'Toque para fechar' : 'Toque para montar sua sessão'}
                                </span>
                            </div>
                        </button>

                        {hasTabs && isAdmin && (
                            <div className="cdp-admin-menu-wrap" ref={adminMenuRef}>
                                <button
                                    className={`cdp-hero-icon-btn ${showAdminMenu ? 'cdp-hero-icon-btn--active' : ''}`}
                                    onClick={() => setShowAdminMenu(p => !p)}
                                    title="Configurações"
                                >
                                    <Settings size={17} />
                                </button>
                                {showAdminMenu && (
                                    <div className="cdp-admin-dropdown">
                                        <button className="cdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setShowTabManager(true); }}>
                                            <Settings size={14} /> Gerenciar Abas
                                        </button>
                                        <button className="cdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setShowImportDialog(true); }}>
                                            <Upload size={14} /> Importar Deck
                                        </button>
                                        <button className="cdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setSelectedTheme(cycle?.headerTheme || 'default'); setShowThemeModal(true); }}>
                                            <Settings size={14} /> Personalizar Header
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Linha 3: busca + gear admin (só sem abas) */}
                {!hasTabs && (
                    <div className="cdp-hero-search-row">
                        <div className="cdp-hero-search">
                            <Search size={14} className="cdp-hero-search-icon" />
                            <input
                                className="cdp-hero-search-input"
                                placeholder="Pesquisar decks..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button className="cdp-hero-search-clear" onClick={() => setSearchQuery('')}>
                                    <X size={12} />
                                </button>
                            )}
                        </div>

                        {isAdmin && (
                            <div className="cdp-admin-menu-wrap" ref={adminMenuRef}>
                                <button
                                    className={`cdp-hero-icon-btn ${showAdminMenu ? 'cdp-hero-icon-btn--active' : ''}`}
                                    onClick={() => setShowAdminMenu(p => !p)}
                                    title="Configurações"
                                >
                                    <Settings size={17} />
                                </button>
                                {showAdminMenu && (
                                    <div className="cdp-admin-dropdown">
                                        <button className="cdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setShowCreateDialog(true); }}>
                                            <Plus size={14} /> Criar Deck
                                        </button>
                                        <button className="cdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setShowTabManager(true); }}>
                                            <Settings size={14} /> Gerenciar Abas
                                        </button>
                                        <button className="cdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setShowImportDialog(true); }}>
                                            <Upload size={14} /> Importar Deck
                                        </button>
                                        <button className="cdp-admin-drop-item" onClick={() => { setShowAdminMenu(false); setSelectedTheme(cycle?.headerTheme || 'default'); setShowThemeModal(true); }}>
                                            <Settings size={14} /> Personalizar Header
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ══ ABAS ══ */}
            {hasTabs && (
                <CycleTabs
                    tabs={tabs}
                    activeTabId={showStudyMode ? 'study' : activeTabId}
                    onTabChange={handleTabChange}
                    isAdmin={isAdmin}
                    hasTabs={hasTabs}
                    showStudyMode={showStudyMode}
                    onStudyToggle={handleStudyToggle}
                    onOpenTabManager={() => setShowTabManager(true)}
                    onCreateDeck={() => setShowCreateDialog(true)}
                    onImportDeck={() => setShowImportDialog(true)}
                />
            )}

            {/* ══ CONTEÚDO ══ */}
            <div className="decks-container">

                {/* Hero "Continuar de onde parou" — card dentro do container */}
                {!showStudyMode && lastStudiedDeck && (
                    <div className="cdp-continue-hero" onClick={handleContinueLastDeck}>
                        <div className="cdp-continue-icon">
                            <BookOpen size={20} />
                        </div>
                        <div className="cdp-continue-body">
                            <span className="cdp-continue-badge">✦ Continuar de onde parou</span>
                            <strong className="cdp-continue-name">{lastStudiedDeck.deckName}</strong>
                            <span className="cdp-continue-meta">
                                {formatRelativeTime(lastStudiedDeck.lastStudiedAt)}
                                {lastStudiedDeck.masteryPercentage > 0 && ` · ${lastStudiedDeck.masteryPercentage}% domínio`}
                            </span>
                        </div>
                        <div className="cdp-continue-right">
                            {lastStudiedDeck.masteryPercentage > 0 && (
                                <div className="cdp-continue-mastery-bar">
                                    <div
                                        className="cdp-continue-mastery-fill"
                                        style={{ width: `${lastStudiedDeck.masteryPercentage}%` }}
                                    />
                                </div>
                            )}
                            <button className="cdp-continue-btn" onClick={e => { e.stopPropagation(); handleContinueLastDeck(); }}>
                                Estudar ↗
                            </button>
                        </div>
                    </div>
                )}

                {/* Estudo personalizado */}
                {showStudyMode && (
                    <CustomStudyInline
                        contextId={cycleId || ''}
                        contextName={cycle.name}
                        prefetchedItems={customStudyCacheDecks}
                        prefetchedSubdecksMap={customStudyCacheSubdecksMap}
                    />
                )}

                {!showStudyMode && !hasTabs && (
                    <DeckGrid
                        decks={decks}
                        isAdmin={isAdmin}
                        isEditor={isEditor}
                        searchQuery={searchQuery}
                        onNavigate={handleViewSubdecks}
                        onEdit={openEditDialog}
                        onDelete={openDeleteDialog}
                        onExport={handleExport}
                        onToggleBlock={handleToggleBlock}
                        onMove={deck => { setDeckToMove(deck); setShowMoveModal(true); }}
                        onCopy={isAdmin ? deck => { setDeckToCopy(deck); setShowCopyModal(true); } : undefined}
                        onToggleHide={handleToggleHide}
                        onReorder={handleReorder}
                        onToggleFree={handleToggleFree}
                        onUpgradeClick={deck => { setSelectedDeck(deck); setShowUpgradeModal(true); }}
                        onInterdictedClick={deck => { setSelectedEntityForModal({ type: 'deck', name: deck.name }); setShowInterdictedModal(true); }}
                        emptyMessage="Nenhum deck ainda."
                        emptyAction={isAdmin ? { label: 'Criar primeiro deck', onClick: () => setShowCreateDialog(true) } : undefined}
                    />
                )}

                {!showStudyMode && hasTabs && activeTab && (
                    <DeckGrid
                        decks={activeTabDecks}
                        isAdmin={isAdmin}
                        isEditor={isEditor}
                        searchQuery={searchQuery}
                        onNavigate={handleViewSubdecks}
                        onEdit={openEditDialog}
                        onDelete={openDeleteDialog}
                        onExport={handleExport}
                        onToggleFree={handleToggleFree}
                        onToggleBlock={handleToggleBlock}
                        onMove={deck => { setDeckToMove(deck); setShowMoveModal(true); }}
                        onCopy={isAdmin ? deck => { setDeckToCopy(deck); setShowCopyModal(true); } : undefined}
                        onToggleHide={handleToggleHide}
                        onReorder={handleReorder}
                        onUpgradeClick={deck => { setSelectedDeck(deck); setShowUpgradeModal(true); }}
                        onInterdictedClick={deck => { setSelectedEntityForModal({ type: 'deck', name: deck.name }); setShowInterdictedModal(true); }}
                        emptyMessage="Esta aba ainda não possui decks."
                        emptyAction={isAdmin ? { label: 'Criar Deck', onClick: () => setShowCreateDialog(true) } : undefined}
                    />
                )}

                {!showStudyMode && hasTabs && !activeTab && (
                    <div className="empty-state">
                        <h2>Selecione uma aba</h2>
                    </div>
                )}
            </div>

            {/* ══ MODAIS ══ */}

            {showTabManager && (
                <TabManagerModal
                    tabs={tabs}
                    cycleId={cycleId || ''}
                    onClose={() => setShowTabManager(false)}
                    onRefresh={loadTabs}
                    setToast={setToast}
                />
            )}

            {/* Criar deck */}
            {showCreateDialog && (
                <div className="dialog-overlay" onClick={closeAllDialogs}>
                    <div className="dialog" onClick={e => e.stopPropagation()}>
                        <h2>Criar Novo Deck</h2>
                        <input className="dialog-input" placeholder="Nome do deck" value={deckName} onChange={e => setDeckName(e.target.value)} autoFocus />
                        <textarea className="dialog-textarea" placeholder="Descrição (opcional)" value={deckDescription} onChange={e => setDeckDescription(e.target.value)} />

                        {/* Color picker */}
                        <div className="dialog-color-section">
                            <label className="dialog-label">Cor do deck</label>
                            <div className="dialog-color-picker">
                                {DECK_COLOR_OPTIONS.map(c => (
                                    <button
                                        key={c.id}
                                        className={`dialog-color-swatch ${deckColorId === c.id ? 'dialog-color-swatch--active' : ''}`}
                                        style={{ background: c.hex }}
                                        onClick={() => setDeckColorId(c.id)}
                                        title={c.label}
                                        type="button"
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Icon picker */}
                        <div className="dialog-color-section">
                            <label className="dialog-label">Ícone do deck</label>
                            <DeckIconPicker value={deckIconId} onChange={setDeckIconId} />
                        </div>

                        <div className="dialog-tags-wrap">
                            <label className="dialog-label">Detalhes do conteúdo</label>
                            <div className="dialog-tag-input-row">
                                <input
                                    className="dialog-input"
                                    placeholder="Ex: Cardiopatias congênitas..."
                                    value={tagInput}
                                    onChange={e => setTagInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                                    style={{ marginBottom: 0 }}
                                />
                                <button className="btn-add-tag" onClick={addTag}>+</button>
                            </div>
                            {deckTags.length > 0 && (
                                <div className="dialog-tags">
                                    {deckTags.map(t => (
                                        <span key={t} className="dialog-tag">
                                            {t}<button onClick={() => removeTag(t)}>×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="dialog-checkbox-row">
                            <input type="checkbox" id="isFreeCheck" checked={deckIsFree} onChange={e => setDeckIsFree(e.target.checked)} />
                            <label htmlFor="isFreeCheck">Marcar como <strong>Gratuito</strong></label>
                        </div>

                        <div className="dialog-actions">
                            <button onClick={closeAllDialogs}>Cancelar</button>
                            <button className="btn-primary" disabled={submitting} onClick={handleCreateDeck}>
                                {submitting ? 'Criando...' : 'Criar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Editar deck */}
            {showEditDialog && currentDeck && (
                <div className="dialog-overlay" onClick={closeAllDialogs}>
                    <div className="dialog" onClick={e => e.stopPropagation()}>
                        <h2>Editar Deck</h2>
                        <input className="dialog-input" placeholder="Nome" value={deckName} onChange={e => setDeckName(e.target.value)} />
                        <textarea className="dialog-textarea" placeholder="Descrição" value={deckDescription} onChange={e => setDeckDescription(e.target.value)} />

                        {/* Color picker */}
                        <div className="dialog-color-section">
                            <label className="dialog-label">Cor do deck</label>
                            <div className="dialog-color-picker">
                                {DECK_COLOR_OPTIONS.map(c => (
                                    <button
                                        key={c.id}
                                        className={`dialog-color-swatch ${deckColorId === c.id ? 'dialog-color-swatch--active' : ''}`}
                                        style={{ background: c.hex }}
                                        onClick={() => setDeckColorId(c.id)}
                                        title={c.label}
                                        type="button"
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Icon picker */}
                        <div className="dialog-color-section">
                            <label className="dialog-label">Ícone do deck</label>
                            <DeckIconPicker value={deckIconId} onChange={setDeckIconId} />
                        </div>

                        <div className="dialog-tags-wrap">
                            <label className="dialog-label">Detalhes do conteúdo</label>
                            <div className="dialog-tag-input-row">
                                <input
                                    className="dialog-input"
                                    placeholder="Adicionar detalhe..."
                                    value={tagInput}
                                    onChange={e => setTagInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                                    style={{ marginBottom: 0 }}
                                />
                                <button className="btn-add-tag" onClick={addTag}>+</button>
                            </div>
                            {deckTags.length > 0 && (
                                <div className="dialog-tags">
                                    {deckTags.map(t => (
                                        <span key={t} className="dialog-tag">
                                            {t}<button onClick={() => removeTag(t)}>×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button className="btn-manage-icon" onClick={() => { setShowEditDialog(false); setShowIconDialog(true); }}>
                            <Upload size={15} /> Upload de Ícone Personalizado
                        </button>

                        <div className="dialog-actions">
                            <button onClick={closeAllDialogs}>Cancelar</button>
                            <button className="btn-primary" disabled={submitting} onClick={handleEditDeck}>
                                {submitting ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Deletar deck */}
            {showDeleteDialog && currentDeck && (
                <div className="dialog-overlay" onClick={closeAllDialogs}>
                    <div className="dialog" onClick={e => e.stopPropagation()}>
                        <h2>Confirmar Exclusão</h2>
                        <p>Excluir o deck "<strong>{currentDeck.name}</strong>"? Esta ação não pode ser desfeita.</p>
                        <div className="dialog-actions">
                            <button onClick={closeAllDialogs}>Cancelar</button>
                            <button className="btn-danger" disabled={submitting} onClick={handleDeleteDeck}>
                                {submitting ? 'Excluindo...' : 'Excluir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showMoveModal && deckToMove && (
                <MoveModal
                    mode="deck"
                    sourceCycleId={cycleId}
                    deckId={deckToMove._id}
                    deckName={deckToMove.name}
                    onClose={() => { setShowMoveModal(false); setDeckToMove(null); }}
                    onSuccess={() => { clearCache(); loadTabs(); loadCycleData(); }}
                    onToast={(msg, type) => setToast({ message: msg, type })}
                />
            )}

            {showCopyModal && deckToCopy && (
                <CopyModal
                    mode="deck"
                    sourceCycleId={cycleId}
                    deckId={deckToCopy._id}
                    deckName={deckToCopy.name}
                    onClose={() => { setShowCopyModal(false); setDeckToCopy(null); }}
                    onSuccess={() => { clearCache(); loadTabs(); loadCycleData(); }}
                    onToast={(msg, type) => setToast({ message: msg, type })}
                />
            )}

            {showIconDialog && currentDeck && (
                <DeckIconManager
                    deckId={currentDeck._id}
                    currentIconUrl={currentDeck.iconUrl || null}
                    onIconUpdate={() => { loadCycleData(); setShowIconDialog(false); }}
                    onClose={() => setShowIconDialog(false)}
                />
            )}

            {showInterdictedModal && selectedEntityForModal && (
                <InterdictedModal
                    isOpen={showInterdictedModal}
                    onClose={() => { setShowInterdictedModal(false); setSelectedEntityForModal(null); }}
                    entityType={selectedEntityForModal.type}
                    entityName={selectedEntityForModal.name}
                />
            )}

            <ImportModal
                isOpen={showImportDialog}
                onClose={() => { setShowImportDialog(false); setImportFiles([]); }}
                onImport={handleImport}
                onFilesSelect={setImportFiles}
                selectedFiles={importFiles}
                type="deck"
                importing={importing}
                acceptZip
                progressLabel={importProgress}
            />

            {showUpgradeModal && selectedDeck && cycle && (
                <UpgradeModal
                    isOpen={showUpgradeModal}
                    onClose={() => { setShowUpgradeModal(false); setSelectedDeck(null); }}
                    entityType="deck"
                    entityName={selectedDeck.name}
                    entityId={selectedDeck._id}
                    cycleName={cycle.name}
                />
            )}

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* ── Modal Personalizar Header ── */}
            {showThemeModal && (
                <div className="sdp-theme-overlay" onClick={() => setShowThemeModal(false)}>
                    <div className="sdp-theme-modal" onClick={e => e.stopPropagation()}>
                        <h3>Personalizar Header</h3>
                        <p className="sdp-theme-modal-sub">Escolha o padrão visual do cabeçalho.</p>
                        <div className="sdp-theme-grid">
                            {HEADER_THEMES.map(t => (
                                <button
                                    key={t.id}
                                    className={`sdp-theme-btn ${selectedTheme === t.id ? 'sdp-theme-btn--active' : ''}`}
                                    onClick={() => setSelectedTheme(t.id)}
                                >
                                    <div style={{ position: 'absolute', inset: 0, opacity: 0.8, pointerEvents: 'none' }}>
                                        <HeaderPattern theme={t.id} />
                                    </div>
                                    <span className="sdp-theme-btn-emoji" style={{ position: 'relative', zIndex: 1 }}>{t.emoji}</span>
                                    <span className="sdp-theme-btn-label" style={{ position: 'relative', zIndex: 1 }}>{t.label}</span>
                                </button>
                            ))}
                        </div>
                        <div className="sdp-theme-modal-actions">
                            <button className="sdp-theme-modal-cancel" onClick={() => setShowThemeModal(false)}>Cancelar</button>
                            <button
                                className="sdp-theme-modal-save"
                                onClick={handleThemeSave}
                                disabled={themeSaving}
                            >
                                {themeSaving ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CycleDecksPage;
